// 검차 사진 섹션 — v1 components/OrderDetail.js:54-86, 301-419 (이슈 288 접힘 320px · 카테고리 5 · 한글 alias · 인증 썸네일 · 개별/전체 다운로드).
// props: order, evalResult, allPhotos (정규화된 배열), thumbBlobs, onView(origIdx)
import React, { useState, useEffect, useRef } from 'react';
import { API_ORIGIN } from '../../../utils/api';
import { parseEvalPhotoUrl } from '../../../utils/evalPhotoUrl';
import { T, S, R, FONT } from '../../theme';
import { pillStyle } from '../../kit/Pill';

// 이슈 288 — 검차 사진 섹션 접힘 높이(px). 이 높이를 넘을 때만 그라데이션 + 확장 버튼 노출.
var PHOTO_COLLAPSED_H = 320;

// 카테고리 색은 CommonUI/InspectionPhotos 의 5색 대신 T 톤으로 (새 색 발명 금지) — 라벨/순서는 v1 그대로.
var CATEGORY_META = {
  exterior: { label: '외관', color: T.accentPress },
  damage: { label: '사고부위', color: T.danger },
  interior: { label: '내관', color: T.ok },
  undercarriage: { label: '하부/엔진', color: T.warn },
  documents: { label: '서류', color: T.navy },
};
var CATEGORY_ORDER = ['exterior', 'damage', 'interior', 'undercarriage', 'documents'];
// 이슈 288 — 검차 앱은 category 를 한글로 업로드. 미지의 값은 exterior 로 폴백 — 사진이 표시 자체가 안 되는 일은 없게.
var CATEGORY_ALIAS = { '외관': 'exterior', '사고부위': 'damage', '내관': 'interior', '하부엔진': 'undercarriage', '하부/엔진': 'undercarriage', '서류': 'documents' };

var dlBtn = { position: 'absolute', bottom: 3, right: 3, width: 22, height: 22, borderRadius: 4, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', padding: 0 };
var memoSt = { position: 'absolute', bottom: 3, left: 3, right: 24, background: 'rgba(0,0,0,0.6)', color: T.card, fontSize: 9, padding: '2px 6px', borderRadius: 4, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
var fade = { position: 'absolute', left: 0, right: 0, bottom: 0, height: 96, background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,1) 100%)', pointerEvents: 'none' };

export function normalizePhotos(evalResult) {
  var p = evalResult && evalResult.photos;
  if (typeof p === 'string') { try { p = JSON.parse(p); } catch (e) { p = []; } }
  if (!Array.isArray(p)) return [];
  return p.filter(function(x) { return x && x.url && !x.url.startsWith('blob:'); });
}

function OrderPhotos(props) {
  var order = props.order, photos = props.allPhotos || [], thumbBlobs = props.thumbBlobs || {};
  var photoBoxRef = useRef(null);
  var stE = useState(false); var expanded = stE[0], setExpanded = stE[1];
  var stO = useState(false); var overflow = stO[0], setOverflow = stO[1];

  // 이슈 288 — 실제 컨텐츠가 접힘 높이를 넘을 때만 토글 노출.
  useEffect(function() {
    if (!photos.length) { setOverflow(false); return undefined; }
    var check = function() {
      var el = photoBoxRef.current;
      if (!el) return;
      setOverflow(el.scrollHeight > PHOTO_COLLAPSED_H + 8);
    };
    check();
    var ro = null;
    if (typeof window.ResizeObserver === 'function' && photoBoxRef.current) {
      ro = new window.ResizeObserver(check);
      ro.observe(photoBoxRef.current);
    }
    window.addEventListener('resize', check);
    return function() {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', check);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  if (!photos.length) return null;

  var byCategory = { exterior: [], damage: [], interior: [], undercarriage: [], documents: [] };
  photos.forEach(function(p, origIdx) {
    var cat = CATEGORY_ALIAS[p.category] || p.category || 'exterior';
    if (!byCategory[cat]) cat = 'exterior';
    byCategory[cat].push({ photo: p, origIdx: origIdx });
  });

  var renderPhotoItem = function(entry, key) {
    var p = entry.photo;
    var origIndex = entry.origIdx;
    // W-12 — url 폴백 경로는 서명 쿼리(?exp=&sig=)가 붙어 확장자 판정이 깨진다. filename 우선, 없으면 쿼리/프래그먼트 제거.
    var vidName = p.filename || String(p.url || '').split(/[?#]/)[0];
    var isVideo = /\.(mp4|mov|avi|webm|3gp)$/i.test(vidName);
    return (
      <div key={key} style={{ position: 'relative', cursor: 'pointer' }} onClick={function() { props.onView(origIndex); }}>
        {/* 이슈 288 — 정사각(1:1) 셀 강제: padding-top 100% 래퍼 + absolute inset (lazy load 시 layout shift 없음) */}
        <div style={{ position: 'relative', width: '100%', paddingTop: '100%', borderRadius: R.md, border: '1px solid ' + T.bd, background: T.head, overflow: 'hidden' }}>
          {isVideo
            ? <video src={p.url} controls style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', background: T.ink }}/>
            : <img src={thumbBlobs[p.url] || ''} alt={p.memo || p.category} loading="lazy" decoding="async" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>
          }
        </div>
        {p.memo && <div style={memoSt}>{p.memo}</div>}
        <button type="button" aria-label="사진 다운로드" onClick={function(e) { e.stopPropagation(); var pp = parseEvalPhotoUrl(p.url); if (pp) window.open(API_ORIGIN + '/api/eval/orders/' + pp.orderId + '/photos/' + encodeURIComponent(pp.filename) + '/download', '_blank'); }} style={dlBtn}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: T.card }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
      </div>
    );
  };

  var renderCategoryGrid = function(catKey) {
    var meta = CATEGORY_META[catKey];
    var items = byCategory[catKey] || [];
    return (
      <div key={catKey} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={pillStyle(meta.color)}>{meta.label}</span>
          <span style={{ fontSize: FONT.label, color: T.faint }}>{items.length}장</span>
        </div>
        {items.length > 0 ? (
          // Wave W4 — auto-fill + minmax 반응형 (데스크탑 max 3col cap)
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))', gap: 6, maxWidth: 3 * 140 }}>
            {items.map(function(entry, i) { return renderPhotoItem(entry, i); })}
          </div>
        ) : (
          <div style={{ background: T.head, borderRadius: R.md, padding: '12px 0', textAlign: 'center', fontSize: 12, color: T.faint }}>미등록</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 16 }} className="v2-eval-photos">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>검차 사진 ({photos.length}장)</div>
        <button type="button" style={Object.assign({}, S.btn, S.btnSm)} onClick={function() { window.open(API_ORIGIN + '/api/eval/orders/' + order.id + '/photos/download-all', '_blank'); }}>전체 다운로드</button>
      </div>
      <div style={{ position: 'relative' }}>
        <div ref={photoBoxRef} style={{ maxHeight: expanded ? 'none' : PHOTO_COLLAPSED_H, overflow: 'hidden' }}>
          {CATEGORY_ORDER.map(function(catKey) {
            if (byCategory[catKey].length === 0) return null;
            return renderCategoryGrid(catKey);
          })}
        </div>
        {!expanded && overflow && <div style={fade}/>}
      </div>
      {overflow && (
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <button type="button" onClick={function() { setExpanded(!expanded); }} style={Object.assign({}, S.btn, S.btnSm, { borderRadius: R.pill })}>{expanded ? '접기 ▴' : '사진 전체 보기 (' + photos.length + '장) ▾'}</button>
        </div>
      )}
    </div>
  );
}

export default OrderPhotos;
